import React, { FCWithChildren, useState } from 'react';
import { createContext } from 'use-context-selector';
import { Alert, AlertNotificationState } from '@interfaces/models/alert';
import AlertsService, { AlertsQuery, NewInsAlertsResponse, PaginationState } from '@services/alerts-service';
import { ApiResponseMeta } from '@interfaces/api';
import logger from '@helpers/utils/logger/client';

type AlertsContextValues = {
  getNewInsAlertsResponse: (alertsQuery: AlertsQuery) => Promise<void>;
  deleteNewInsAlert: (id: string) => Promise<void>;
  updateAlertNotificationState: (id: string, state: AlertNotificationState) => Promise<void>;
  userNewInsAlertsResponse: NewInsAlertsResponse;
  setUserNewinsAlertsResponse: React.Dispatch<React.SetStateAction<NewInsAlertsResponse>>;
  alertsQuery: AlertsQuery;
  setAlertsQuery: React.Dispatch<React.SetStateAction<AlertsQuery>>;
  DEFAULT_LIMIT: number;
};

const AlertsContext = createContext<AlertsContextValues>({} as AlertsContextValues);

const getPaginationState = ({ limit, offset, count }: ApiResponseMeta): PaginationState => {
  // "limit" here is page count
  // show elements as a single page if no elements
  if (count <= limit) {
    return {
      currentPage: 0,
      totalPages: 0,
      totalCount: count,
    };
  }

  const totalPages = Math.ceil(count / limit);
  const currentPage = offset / limit + 1;
  return {
    totalPages,
    currentPage,
    totalCount: count,
  };
};

const DEFAULT_LIMIT = 16;

const AlertsProvider: FCWithChildren = (props): React.JSX.Element => {
  const { children } = props;
  const [userNewInsAlertsResponse, setUserNewinsAlertsResponse] = useState<NewInsAlertsResponse>(null);
  const [alertsQuery, setAlertsQuery] = useState<AlertsQuery | null>({
    limit: DEFAULT_LIMIT,
    offset: 0,
  });
  const getNewInsAlertsResponse = async (alertQuery: AlertsQuery): Promise<void> => {
    try {
      const { data, meta } = await AlertsService.getAlertsNewin(alertQuery);
      setUserNewinsAlertsResponse({
        alerts: data,
        metaData: getPaginationState(meta),
      });
    } catch (error) {
      logger.error(`alerts::fetchNewInAlerts::${alertQuery}`, error);
      throw error;
    }
  };

  const deleteNewInsAlert = async (alertId: string): Promise<void> => {
    try {
      await AlertsService.deleteAlert(alertId, 'alert_newIn');
      await getNewInsAlertsResponse({ limit: DEFAULT_LIMIT, offset: 0 });
    } catch (error) {
      logger.error(`alerts::deleteAnAlert::${alertId}`, error?.detail);
      throw error?.detail;
    }
  };

  const updateAlertNotificationState = async (
    alertId: string,
    notificationState: AlertNotificationState,
  ): Promise<void> => {
    try {
      await AlertsService.updateNotificationState(alertId, notificationState);
      const alertList: Alert[] = userNewInsAlertsResponse?.alerts.map((alert) => {
        return alert.id === alertId ? { ...alert, notificationState: notificationState.enabled } : alert;
      });
      const newAlertListState = {
        ...userNewInsAlertsResponse,
        alerts: alertList,
      };
      setUserNewinsAlertsResponse(newAlertListState);
    } catch (error) {
      logger.error(`alerts::updateAlertNotification::${alertId}`, error);
      throw error;
    }
  };

  const value = {
    getNewInsAlertsResponse,
    deleteNewInsAlert,
    updateAlertNotificationState,
    userNewInsAlertsResponse,
    setUserNewinsAlertsResponse,
    alertsQuery,
    setAlertsQuery,
    DEFAULT_LIMIT,
  };

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
};

export { AlertsProvider, AlertsContext };
